import { supabase } from "../lib/supabase";
import { fetchBookingStorageBlobViaEdge } from "./bookingStorageFileViaEdge";

async function loadBookingInvoiceBlob(
  cleanPath: string,
  bookingId?: number
): Promise<Blob> {
  if (bookingId != null && Number.isFinite(bookingId)) {
    return fetchBookingStorageBlobViaEdge({
      bucket: "bookinginvoices",
      objectPath: cleanPath,
      bookingId,
    });
  }
  const { data, error } = await supabase.storage
    .from("bookinginvoices")
    .download(cleanPath);
  if (error || !data) {
    throw error ?? new Error("Could not download file");
  }
  return data;
}

/**
 * Sanitize filename to prevent special characters and spaces
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return `file_${Date.now()}`; // Handle undefined or null file names
  
  return fileName
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/[^\w.-]/g, "") // Remove any non-alphanumeric characters except dots and hyphens
    .normalize("NFD") // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics
};

/**
 * Generate a file path for booking invoice using company ID as the root folder
 */
export const generateInvoiceFilePath = (
  companyId: number | string,
  bookingId: number | string,
  fileName: string
): string => {
  // Sanitize the filename
  const sanitizedFileName = sanitizeFileName(fileName);
  
  // Create path with company_id as root
  return `${companyId}/${bookingId}/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Upload a booking invoice file using the company-based path structure
 */
export const uploadBookingInvoice = async (
  bookingId: number | string,
  file: File,
  companyId: number | string = 0 // Make companyId optional with a default value
): Promise<{ error: unknown; path?: string } | null> => {
  try {
    if (!file) {
      console.error("No file provided for upload");
      return { error: "No file provided" };
    }
    
    console.log("uploadBookingInvoice starting with:", {
      bookingId,
      companyId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    // Get company ID from booking if not provided
    const finalCompanyId = companyId || 0; // Use 0 as default company ID if not provided
    
    // Generate path with company ID as root
    const filePath = generateInvoiceFilePath(finalCompanyId, bookingId, file.name);
    
    console.log("Generated file path:", filePath);
    
    // Upload to Supabase storage
    console.log("Uploading to bucket:", "bookinginvoices");
    const { data, error } = await supabase.storage
      .from("bookinginvoices")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
    
    if (error) {
      console.error("Error uploading booking invoice:", error);
      return { error };
    }
    
    console.log("Booking invoice uploaded successfully:", data.path);
    return { error: null, path: data.path };
  } catch (error) {
    console.error("Error in uploadBookingInvoice:", error);
    return { error };
  }
};

/**
 * Get public URL for a booking invoice file
 */
export const getBookingInvoicePublicUrl = (
  path: string
): string | null => {
  try {
    if (!path || path.trim() === '') {
      return null;
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    const { data } = supabase.storage
      .from("bookinginvoices")
      .getPublicUrl(cleanPath);
    
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting public URL:", error);
    return null;
  }
};

/**
 * Create a signed URL for booking invoice file
 */
export const createBookingInvoiceSignedUrl = async (
  path: string,
  expiresIn: number = 60,
  bookingId?: number
): Promise<string | null> => {
  void expiresIn;
  try {
    if (!path || path.trim() === '') {
      return null;
    }

    const cleanPath = path.trim().replace(/^\//, "");

    const blob = await loadBookingInvoiceBlob(cleanPath, bookingId);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error in createBookingInvoiceSignedUrl:", error);
    return null;
  }
};

/**
 * Download a booking invoice file
 */
export const downloadBookingInvoice = async (
  path: string,
  filename: string,
  bookingId?: number
): Promise<void> => {
  try {
    if (!path || path.trim() === '') {
      throw new Error("Invalid file path provided");
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    console.log("Downloading booking invoice:", cleanPath);
    
    const fileExt = cleanPath.split(".").pop()?.toLowerCase() || "pdf";

    const blob = await loadBookingInvoiceBlob(cleanPath, bookingId);

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${filename}.${fileExt}`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log("Download completed for:", filename);
  } catch (error) {
    console.error("Error downloading booking invoice:", error);
    throw error;
  }
}; 